<template>
  <div class="product-details">
    <div class="product-details__header product-header">
      <h2 class="product-header__title u-typography-helvetica u-text-display u-text-display--s">
        {{ productTitle }}
      </h2>
      <p class="product-header__subtitle u-typography-helvetica u-text u-text--s">
        {{ $t(productSubtitle) }}
      </p>
      <div class="product-header__labels product-labels flex--align-start">
        <product-tag
          v-if="isRecommended"
          :is-product-recommended="isRecommended"
          :message="$t('product.drSelectionMessage')"
          class="product-info-labels__recommended-label product-label"
        ></product-tag>

        <product-tag
          v-if="isAddToCartDisabled"
          :is-product-available="isProductAvailable"
          :is-product-in-stock="isProductInStock"
          :message="statusTagMessage"
          class="product-labels__status-label product-label"
        />

        <product-tag
          v-for="productCategory of productCategories"
          :key="productCategory"
          :message="productCategory"
          class="product-labels__category product-label product-label--category"
        />

        <product-tag
          v-if="productUsage"
          :message="productUsage"
          class="product-labels__usage product-label"
        />
      </div>
    </div>

    <div
      class="product-details__product-description product-description u-typography-helvetica u-text u-text--s"
    >
      <text-clamped :max-rows="4">
        <p class="product-description__text">
          {{ $t(introDescription) }}
        </p>

        <template #show-all-trigger>
          <span
            class="product-description__read-more u-typography-helvetica u-text u-text--link u-text--xs"
            >{{ $t('common.readMore') }}</span
          >
        </template>
      </text-clamped>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

import ProductTag from '@/components/common/ProductTag.vue';
import TextClamped from '@/components/common/TextClamped.vue';

import { priceFormatterMixin } from '@/mixins/priceFormatterMixin';

import { getProductOptionsDefaultOption } from '@/models/core/ProductOptions';
import {
  isProductPackAvailable,
  isProductPackInStock,
  getProductPackUsage
} from '@/models/core/ProductPack';

import { types } from '@/store/types';
import {
  COMPLEMENTARY_PRODUCT_INTRO_DESCRIPTION,
  COMPLEMENTARY_PRODUCT_USAGE,
  COMPLEMENTARY_PRODUCT_SUBTITLE_TRANSLATION_KEY
} from '@/constants/discover';
import { PRODUCTS_CONFIGURATION } from '@/constants/your-routine';
import { PRODUCT_CATEGORY, PRODUCT_NAME_TO_CATEGORIES_LIST_MAP } from '@/constants/treatment';

const CATEGORY_DISPLAY_VALUE = {
  [PRODUCT_CATEGORY.CLEANSE]: 'product.cleanseMessage',
  [PRODUCT_CATEGORY.PROTECT]: 'product.protectMessage',
  [PRODUCT_CATEGORY.MOISTURIZE]: 'product.moisturizeMessage',
  [PRODUCT_CATEGORY.TREAT]: 'product.treatMessage'
};

export default {
  name: 'ComplementaryProductDescription',
  components: {
    ProductTag,
    TextClamped
  },
  mixins: [priceFormatterMixin],
  props: {
    productName: {
      type: String,
      required: true
    }
  },
  computed: {
    ...mapGetters({
      allProductOptions: types.getters.ALL_PRODUCT_OPTIONS_SETS,
      getIsProductRecommended: types.getters.GET_IS_PRODUCT_RECOMMENDED
    }),
    product() {
      return getProductOptionsDefaultOption(this.allProductOptions[this.productName]);
    },
    productConfiguration() {
      return this.getProductConfiguration(this.product);
    },
    productTitle() {
      const { title } = PRODUCTS_CONFIGURATION[this.productName];

      return this.$t(title);
    },
    productSubtitle() {
      return COMPLEMENTARY_PRODUCT_SUBTITLE_TRANSLATION_KEY[this.productName];
    },
    productCategories() {
      return PRODUCT_NAME_TO_CATEGORIES_LIST_MAP[this.productName].map(
        category => CATEGORY_DISPLAY_VALUE[category]
      );
    },
    productUsage() {
      const usageConfig = COMPLEMENTARY_PRODUCT_USAGE[this.productName];
      const productUsage = getProductPackUsage(this.product);
      const productUsageKey = usageConfig[productUsage] || usageConfig;

      return this.$t(productUsageKey);
    },
    isRecommended() {
      return this.getIsProductRecommended(this.productName);
    },
    isProductInStock() {
      return isProductPackInStock(this.product);
    },
    isProductAvailable() {
      return isProductPackAvailable(this.product);
    },
    isAddToCartDisabled() {
      return !this.isProductInStock || !this.isProductAvailable;
    },
    introDescription() {
      return COMPLEMENTARY_PRODUCT_INTRO_DESCRIPTION[this.productName][0];
    },
    statusTagMessage() {
      return !this.isProductAvailable
        ? this.$t('product.comingSoonMessage')
        : this.$t('product.outOfStockMessage');
    }
  }
};
</script>

<style lang="scss" scoped>
.product-details {
  &__product-description {
    margin-bottom: 28px;
  }
}

.product-header {
  &__title,
  &__subtitle {
    margin-bottom: 8px;
  }

  &__title {
    color: var(--u-color-graphite);
  }

  &__labels {
    display: flex;
    flex-wrap: wrap;
    margin-bottom: 10px;
  }
}

.product-description {
  &__read-more {
    margin-top: 20px;
    display: inline-block;
    cursor: pointer;
  }
}

.product-label {
  margin-right: 12px;
  margin-bottom: 15px;

  &--category {
    text-transform: uppercase;
  }
}

@media (min-width: 960px) {
  .product-details {
    max-width: 580px;
  }

  .product-header {
    &__title {
      margin-bottom: 8px;
    }
  }
}
</style>
